import React, { useState } from "react";
import styled from "styled-components";
import { useQuery, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { votingApi, openMyOwnPdfHandler, open3rdPartyPdfHandler } from "./helpers/fetcher";
import PDFBtn from "./components/PDFBtn";
import NProgress from "nprogress";
import "nprogress/nprogress.css";
import Tooltip from '@mui/material/Tooltip'
import { requestTranslation } from "@futures-platform/i18n";


// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Styled Components
const ReportContainer = styled.div`
  // max-width: 900px;
  margin: 0 auto;
  padding: 1rem 5% 0px;
  font-family: L10,Roboto,"Helvetica Neue",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol";
  min-height: 100vh;
  color: #121212;
`;

const ReportCard = styled.div`
  overflow: hidden;
  animation: slideUp 0.5s ease-out;
  
  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ReportHeader = styled.div`
  // padding: 32px;
`;

const ReportTitle = styled.h1`
  margin: 0 0 0 0;
  font-size: 30px;
  font-weight: 700;
  line-height: 36px;
  letter-spacing: .114286px;
  color: inherit;
`;

const MetadataGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
  margin-top: 22px;
`;

const MetadataItem = styled.div`
  // padding: 16px;
`;

const MetadataLabel = styled.div`
  font-size: 12px;
  // text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 6px;
  font-weight: 400;
`;

const MetadataValue = styled.div`
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
`;

const ReportBody = styled.div`
  // padding: 32px;
  padding: 26px 0 32px 0;
`;

const SectionTitle = styled.h2`
  font-size: 23px;
  font-weight: 700;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
`;

const SummaryContent = styled.div`
  font-size: 14px;
  line-height: 21px;
  margin-bottom: 32px;
  // padding: 20px;
  // padding: 20px 0 20px 0;
  
  h1, h2, h3, h4, h5, h6 {
    margin: 20px 0 12px 0;
    font-weight: 700;
    line-height: 1.3;
    
    &:first-child {
      margin-top: 0;
    }
  }
  
  h1 { font-size: 28px; }
  h2 { font-size: 24px; }
  h3 { font-size: 20px; }
  h4 { font-size: 18px; }
  h5 { font-size: 16px; }
  h6 { font-size: 14px; }
  
  p {
    margin: 0 0 16px 0;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  ul, ol {
    margin: 0 0 16px 0;
    padding-left: 24px;
    
    li {
      margin-bottom: 8px;
    }
  }
  
  strong, b {
    font-weight: 600;
  }
  
  em, i {
    font-style: italic;
  }
  
  a {
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  blockquote {
    padding-left: 16px;
    margin: 16px 0;
    font-style: italic;
  }
  
  code {
    padding: 2px 6px;
    border-radius: 4px;
    font-family: 'Courier New', monospace;
    font-size: 14px;
  }
  
  pre {
    padding: 16px;
    border-radius: 8px;
    overflow-x: auto;
    margin: 16px 0;
    
    code {
      padding: 0;
    }
  }
  
  table {
    width: 100%;
    border-collapse: collapse;
    margin: 16px 0;
    
    th, td {
      padding: 12px;
      text-align: left;
    }
    
    th {
      font-weight: 600;
    }
  }
  
  hr {
    border: none;
    margin: 24px 0;
  }
`;


const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 20px;
`;

const Spinner = styled.div`
  width: 50px;
  height: 50px;
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-top: 4px solid rgba(0, 0, 0, 0.5);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  font-size: 18px;
  font-weight: 500;
`;

const ErrorContainer = styled.div`
  border-radius: 12px;
  padding: 24px;
  margin: 24px;
`;

const ErrorTitle = styled.h3`
  margin: 0 0 8px 0;
  font-size: 20px;
  font-weight: 700;
`;

const ErrorMessage = styled.p`
  margin: 0;
  font-size: 16px;
  line-height: 1.6;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 24px;
`;

const EmptyStateIcon = styled.div`
  font-size: 64px;
  margin-bottom: 16px;
`;

const EmptyStateText = styled.div`
  font-size: 20px;
  font-weight: 500;
`;

// Format date helper
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    return dateString;
  }
};

// Report Display Component
const ReportDisplay = ({ pdfId, pdfType, gid }) => {
  const [creatorName, setCreatorName] = useState('');
  const [updaterName, setUpdaterName] = useState('');

  // Fetch report data
  const { data: reportData, isLoading, error } = useQuery({
    queryKey: ['report', pdfId],
    queryFn: async () => {
      NProgress.start();
      try {
        const payload = {
          reports: [pdfId],
          excludedPdfUuids: [],
          groups: [gid],
          size: 12,
          page: 0,
          archived: false,
          include_archived: false,
          language: null,
          query: "",
          sort_method: { by: "df", order: "desc" }
        };
        
        const response = await votingApi.fetchReportData(payload);
        console.log('Report API Response:', response);
        
        // Ensure we never return undefined - return null if data is not available
        if (!response) {
          console.warn('No response from API');
          return null;
        }
        
        if (!response.data) {
          console.warn('No data in response:', response);
          return null;
        }
        
        if (!response.data.result || !Array.isArray(response.data.result)) {
          console.warn('No result array in response.data:', response.data);
          return null;
        }
        
        const reportItem = response.data.result[0];
        if (!reportItem) {
          console.warn('No report item found at index 0');
          return null;
        }
        
        console.log('Report Item Found:', reportItem);
        return reportItem;
      } catch (error) {
        console.error('Error fetching report:', error);
        throw error;
      } finally {
        NProgress.done();
      }
    },
    enabled: !!pdfId
  });

  // Fetch creator name
  useQuery({
    queryKey: ['user', reportData?.created_by],
    queryFn: async () => {
      if (!reportData?.created_by) return null;
      try {
        const response = await votingApi.getUserName(reportData.created_by);
        console.log('Updater API Response:', response);

        const userData = response.data ?? null;
        setCreatorName(userData?.displayName || userData?.username || 'Unknown');
        return userData;
      } catch (error) {
        console.error('Error fetching creator:', error);
        setCreatorName('Unknown');
        return null;
      }
    },
    enabled: !!reportData?.created_by
  });

  // Fetch updater name
  useQuery({
    queryKey: ['user', reportData?.updated_by],
    queryFn: async () => {
      console.log('Updating updater name for:', reportData?.updated_by);
      if (!reportData?.updated_by) return null;
      try {
        const response = await votingApi.getUserName(reportData.updated_by);
        console.log('Updater API Response:', response);
        const userData = response.data ?? null;
        setUpdaterName(userData?.displayName || userData?.username || 'Unknown');
        return userData;
      } catch (error) {
        console.error('Error fetching updater:', error);
        setUpdaterName('Unknown');
        return null;
      }
    },
    enabled: !!reportData?.updated_by
  });

  const handleOpenPDF = () => {
    if (!reportData) return;
    
    const pdfData = {
      id: pdfId || reportData.id,
      filename: reportData.filename || reportData.url,
      ...reportData
    };

    if (pdfType === 'pdf') {
      openMyOwnPdfHandler(pdfData);
    } else if (pdfType === 'statzonpdf') {
      open3rdPartyPdfHandler(pdfData);
    } else {
      // Default to own PDF handler
      openMyOwnPdfHandler(pdfData);
    }
  };

  if (isLoading) {
    return (
      <ReportContainer>
        <LoadingContainer>
          <Spinner />
          <LoadingText>Loading report data...</LoadingText>
        </LoadingContainer>
      </ReportContainer>
    );
  }

  if (error) {
    return (
      <ReportContainer>
        <ErrorContainer>
          <ErrorTitle>⚠️ Error Loading Report</ErrorTitle>
          <ErrorMessage>
            {error.message || 'An error occurred while loading the report. Please try again later.'}
          </ErrorMessage>
        </ErrorContainer>
      </ReportContainer>
    );
  }

  if (!reportData) {
    return (
      <ReportContainer>
        <EmptyState>
          <EmptyStateIcon>📭</EmptyStateIcon>
          <EmptyStateText>No report data available</EmptyStateText>
        </EmptyState>
      </ReportContainer>
    );
  }

  return (
    <ReportContainer>
      <ReportCard>
        <ReportHeader>
          <ReportTitle>{reportData.content?.title || reportData.title || 'Untitled Report'}</ReportTitle>
          
          <MetadataGrid>
            <MetadataItem>
              <MetadataLabel>Uploaded</MetadataLabel>
              <MetadataValue>
                {formatDate(reportData.created_at)}
                {creatorName && <div style={{ marginTop: '4px' }}>by {creatorName}</div>}
              </MetadataValue>
            </MetadataItem>
            
            {/* <MetadataItem>
              <MetadataLabel>Updated</MetadataLabel>
              <MetadataValue>
                {formatDate(reportData.updated_at)}
                {updaterName && <div style={{ marginTop: '4px' }}>by {updaterName}</div>}
              </MetadataValue>
            </MetadataItem> */}
            
            {reportData.meta?.['Publish date'] && (
              <MetadataItem>
                <MetadataLabel>Publish date</MetadataLabel>
                <MetadataValue>{reportData.meta['Publish date']}</MetadataValue>
              </MetadataItem>
            )}
            
            {reportData.locations?.[0]?.name && (
              <MetadataItem>
                <MetadataLabel>Location</MetadataLabel>
                <MetadataValue>{reportData.locations[0].name}</MetadataValue>
              </MetadataItem>
            )}
            {!!reportData?.providers?.length && (
              <MetadataItem>
                <MetadataLabel>Providers</MetadataLabel>
                <MetadataValue>
                  {reportData.providers.map((provider, index) => (
                    <span key={provider.id || index}>
                      {provider.name}
                      {index < reportData.providers.length - 1 && ', '}
                    </span>
                  ))}
                </MetadataValue>
              </MetadataItem>
            )}
            {/* {reportData.type && (
              <MetadataItem>
                <MetadataLabel>Type</MetadataLabel>
                <MetadataValue>{reportData.type.toUpperCase()}</MetadataValue>
              </MetadataItem>
            )}
            
            {reportData.language && (
              <MetadataItem>
                <MetadataLabel>Language</MetadataLabel>
                <MetadataValue>{reportData.language.toUpperCase()}</MetadataValue>
              </MetadataItem>
            )} */}
          </MetadataGrid>
        </ReportHeader>
         <Tooltip
          // placement="bottom-end"
          // title="Open PDF"
          // arrow
          >
            <div style={{display: 'flex', justifyContent: 'flex-start', paddingTop: '1.1rem'}}>
              <PDFBtn handler={handleOpenPDF} isAuthed={true}/>
            </div>
          </Tooltip>
        
        <ReportBody>
          {reportData.content?.summary && (
            <>
              {/* <SectionTitle>Summary</SectionTitle> */}
              <SummaryContent dangerouslySetInnerHTML={{ __html: reportData.content?.summary || reportData.content?.body }} />
            </>
          )}
          
          <div id="pdf-fallback-container"></div>
{/*           
          <button className="btn btn-md btn-primary" onClick={handleOpenPDF}>
            📄 Open PDF Document
          </button> */}
        </ReportBody>
      </ReportCard>
    </ReportContainer>
  );
};

// Main App Component
const App = ({  pdfType, pdfId, position, groupRadarId, uri, gid }) => {
  return (
    <QueryClientProvider client={queryClient}>
      <ReportDisplay pdfId={pdfId} pdfType={pdfType} gid={gid} />
    </QueryClientProvider>
  );
};

export default App;
